// MODULES
import React from "react";
import PropTypes from "prop-types";
import {v4 as generateGUID} from "uuid";

// CLASSES
import Session from "@classes/Session";

// COMPONENTS
import DefaultFields from "../../components/DefaultFields/DefaultFields";
import ActivityIndicator from "@components/ActivityIndicator/ActivityIndicator";

// HELPERS
import {createClassName} from "@helpers/utils";
import fulfillmentCheck from "./AmountSelection.fulfillment";
import Button from "../../../../components/Button/Button";
import CheckboxWithName from "../../components/CheckboxWithName/CheckboxWithName";
import OptionsList from "../../components/OptionsList/OptionsList";
import EditableField from "../../components/EditableField/EditableField";
import NumberField from "../../components/NumberField/NumberField";
import api from "../../../../helpers/api";
import * as CobHelpers from "../../Cob.helpers";

import swishLogo from "@assets/img/swish-logo.svg";

export default class AmountSelectionSwishMC extends React.PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    session: PropTypes.instanceOf(Session).isRequired,
    sectionNumber: PropTypes.number.isRequired,
    number: PropTypes.number.isRequired,
    prevState: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    toggleVisibleSection: PropTypes.func.isRequired
  };

  static companyExclusive = false;
  static privateExclusive = false;
  static fields = _getFields();

  isMobileOrTablet = _isMobileOrTablet();
  frontEndURL =
    window.location.protocol +
    "//" +
    window.location.host +
    "/swish-sandbox/index.html";
  accentColors = this.props.partner._data.accentColors;
  swishSettings = this.props.partner._data.swish;
  apiBaseURL = this.swishSettings.apiBaseURL;
  apiKey = this.swishSettings.apiKey;
  paymentStatusCheck = null;
  payeePaymentReference = generateGUID().slice(0, -4).toUpperCase(); // generate guid and convert to 32 uppercase chars
  PAYMENT_STATUS_CHECK_INTERVAL_MS = 5000;

  initialState = {
    showSwish: false,
    swishFieldsEnabled: !this.swishSettings.requirePersonalInfo,
    token: "",
    identifier: "",
    qrImageURL: "",
    swishStatus: "NOT-LOADED",
    swishPaymentId: null,
    fields: {
      customer_autogiro_single_amount_other: "",
      customer_autogiro_monthly_amount_other: "",

      checkbox_autogiro_single: {
        checked: false
      },

      checkbox_autogiro_monthly: {
        checked: false
      },

      customer_autogiro_single_amount: {
        options: [
          {label: "100 kr", value: "100", checked: false},
          {label: "500 kr", value: "500", checked: false},
          {label: "Valfritt", value: "other", checked: false}
        ]
      },

      customer_autogiro_monthly_amount: {
        options: [
          {label: "150 kr", value: "150", checked: false},
          {label: "200 kr", value: "200", checked: false},
          {label: "Valfritt", value: "other", checked: false}
        ]
      }
    },
    validFields: {
      customer_autogiro_single_amount_other: false,
      customer_autogiro_monthly_amount_other: false
    },
    inputErrors: {
      customer_autogiro_single_amount_other: false,
      customer_autogiro_monthly_amount_other: false
    }
  };

  state = _getInitialState.call(this);

  componentWillUnmount() {
    window.clearInterval(this.paymentStatusCheck);
  }

  componentDidUpdate(prevProps, prevState) {
    const {props, state} = this;
    const {id, onChange} = props;

    if (prevState.fields !== state.fields) {
      onChange(id, {...state}, fulfillmentCheck(props, state));
    }

    if (
      prevProps.app.fulfilledViewsById.CustomerInformation !==
      props.app.fulfilledViewsById.CustomerInformation
    ) {
      if (this.swishSettings.requirePersonalInfo) {
        const isPersonalInfoFulfilled =
          props.app.fulfilledViewsById.hasOwnProperty("CustomerInformation") &&
          props.app.fulfilledViewsById.CustomerInformation === true;

        if (isPersonalInfoFulfilled) {
          this.setState({...this.state, swishFieldsEnabled: true});
        } else {
          this.setState(this.initialState);
          this.props.toggleVisibleSection("AccountDetails", false);
        }
      }
    }
  }

  render() {
    const {props, state} = this;
    const {title} = props;

    const classes = createClassName("Cob-Swish-MC", {
      "Cob-View": true,
      fulfilled: state.isFulfilled
    });

    const checkboxMonthlyText = state.fields.checkbox_autogiro_single.checked
      ? "Jag vill även bli månadsgivare"
      : "Jag vill bli månadsgivare";

    let amountMonthly = 0;
    if (state.fields.checkbox_autogiro_monthly.checked) {
      if (state.fields.customer_autogiro_monthly_amount.options[0].checked) {
        amountMonthly =
          state.fields.customer_autogiro_monthly_amount.options[0].value;
      } else if (
        state.fields.customer_autogiro_monthly_amount.options[1].checked
      ) {
        amountMonthly =
          state.fields.customer_autogiro_monthly_amount.options[1].value;
      } else if (
        state.fields.customer_autogiro_monthly_amount.options[2].checked
      ) {
        amountMonthly = state.fields.customer_autogiro_monthly_amount_other;
      }
    }

    let amountSingle = 0;
    if (state.fields.checkbox_autogiro_single.checked) {
      if (state.fields.customer_autogiro_single_amount.options[0].checked) {
        amountSingle =
          state.fields.customer_autogiro_single_amount.options[0].value;
      } else if (
        state.fields.customer_autogiro_single_amount.options[1].checked
      ) {
        amountSingle =
          state.fields.customer_autogiro_single_amount.options[1].value;
      } else if (
        state.fields.customer_autogiro_single_amount.options[2].checked
      ) {
        amountSingle = state.fields.customer_autogiro_single_amount_other;
      }
    }

    return (
      <div className={classes}>
        <DefaultFields fields={this.constructor.fields} />

        <div className="Cob-Fields">
          <h2>{title}</h2>

          <CheckboxWithName
            type="radio"
            style={{marginTop: "10px"}}
            label="Jag vill skänka ett engångsbelopp"
            name="checkbox_autogiro_single"
            value={state.fields.checkbox_autogiro_single.checked ? "yes" : "no"}
            checked={state.fields.checkbox_autogiro_single.checked}
            onChange={this._onCheckboxChange}
            disabled={!state.swishFieldsEnabled}
          />

          {state.fields.checkbox_autogiro_single.checked && (
            <React.Fragment>
              <div className={"amount-options"}>
                <OptionsList
                  type="radio"
                  orientation="horizontal"
                  name="customer_autogiro_single_amount"
                  options={state.fields.customer_autogiro_single_amount.options}
                  onChange={this._onOptionsListChange}
                />

                {state.fields.customer_autogiro_single_amount.options[2]
                  .checked && (
                  <div
                    style={{
                      margin: "20px 0 0 0",
                      display: "block"
                      // maxWidth: "200px"
                    }}
                  >
                    <strong style={{margin: "0 0 15px 0", display: "block"}}>
                      Ange ett belopp
                    </strong>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: this.isMobileOrTablet ? "column" : "row"
                      }}
                    >
                      <EditableField
                        Component={NumberField}
                        name="customer_autogiro_single_amount_other"
                        label="Valfritt belopp"
                        value={
                          state.fields.customer_autogiro_single_amount_other
                        }
                        onChange={this._onFieldChange}
                        onBlur={this._onFieldBlur}
                        unit={"kr"}
                      />
                      <Button
                        label="Submit"
                        accentColor={this.accentColors.secondary}
                        onClick={this._handleCustomAmountSubmit}
                        style={{
                          ...(this.isMobileOrTablet
                            ? {marginTop: "18px"}
                            : {marginLeft: "25px"})
                        }}
                      />
                    </div>
                    <div
                      style={{
                        color: "#ff5642",
                        height: "22px",
                        alignItems: "flex-start"
                      }}
                    >
                      {this.state.inputErrors
                        .customer_autogiro_single_amount_other && (
                        <p>
                          <b>Please enter the amount you swish to donate.</b>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <input
                  type={"hidden"}
                  name={"customer_autogiro_amount_onetime"}
                  value={amountSingle + " kr"}
                />
              </div>

              {/* {(state.fields.customer_autogiro_single_amount.options[0]
                .checked ||
                state.fields.customer_autogiro_single_amount.options[1]
                  .checked ||
                state.fields.customer_autogiro_single_amount.options[2]
                  .checked) &&
                !state.showSwish && (
                  <span className="link" onClick={this._showSwish}>
                    Klicka här för att göra engångsöverföringen direkt med Swish
                  </span>
                )} */}

              {(state.fields.customer_autogiro_single_amount.options[0]
                .checked ||
                state.fields.customer_autogiro_single_amount.options[1]
                  .checked ||
                state.fields.customer_autogiro_single_amount.options[2]
                  .checked) &&
                state.showSwish && (
                  <div className={"pay-by-swish"}>
                    <img
                      src={swishLogo}
                      className={"logo"}
                      alt={"Logo: Swish"}
                    />

                    <div
                      className={"inner"}
                      style={{
                        width: "100%",

                        minHeight: "232px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      {(state.swishStatus === "LOADED" ||
                        state.swishStatus === "APP-STARTED") && (
                        <ActivityIndicator busy />
                      )}
                      {state.swishStatus === "ERROR" && (
                        <div>
                          <strong>
                            Ursäkta, något gick fel med din betalning
                          </strong>
                        </div>
                      )}
                      {state.swishStatus === "DECLINED" && (
                        <React.Fragment>
                          <strong>
                            It seems that you've cancelled your donation.
                          </strong>
                          <Button
                            label="Back"
                            accentColor={this.accentColors.secondary}
                            onClick={this._handleResetSwish}
                            style={{
                              marginTop: "25px"
                            }}
                          />
                        </React.Fragment>
                      )}
                      {state.swishStatus === "PAID" && (
                        <div>
                          <strong>
                            Success! Many thanks for your donation :)
                          </strong>
                        </div>
                      )}
                      {state.swishStatus === "APP-STARTED" && (
                        <div style={{marginTop: "10px"}}>
                          <strong>
                            Waiting for payment through your Swish app...
                          </strong>
                          <Button
                            label="Cancel"
                            accentColor={this.accentColors.secondary}
                            onClick={this._handleResetSwish}
                            style={{
                              marginTop: "25px"
                            }}
                          />
                        </div>
                      )}
                      {state.fields.customer_autogiro_single_amount.options[0]
                        .checked &&
                        state.swishStatus === "CREATED" && (
                          <React.Fragment>
                            <img
                              src={state.qrImageURL}
                              alt={"Swisha 100 kr"}
                              title={"Swisha 100 kr"}
                            />
                            <strong>
                              Skanna koden ovan i din Swishapp och skänk 100 kr
                            </strong>
                            {this.isMobileOrTablet && (
                              <React.Fragment>
                                <div>
                                  <p>OR</p>
                                </div>
                                <OpenSwishButton
                                  accentColor={this.accentColors.secondary}
                                  onClick={this._handlePayWithApp}
                                />
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        )}

                      {state.fields.customer_autogiro_single_amount.options[1]
                        .checked && (
                        <React.Fragment>
                          {state.swishStatus === "CREATED" && (
                            <React.Fragment>
                              <img
                                src={state.qrImageURL}
                                alt={"Swisha 500 kr"}
                                title={"Swisha 500 kr"}
                              />
                              <strong>
                                Skanna koden ovan i din Swishapp och skänk 500
                                kr
                              </strong>
                              {this.isMobileOrTablet && (
                                <React.Fragment>
                                  <div>
                                    <p>OR</p>
                                  </div>
                                  <OpenSwishButton
                                    accentColor={this.accentColors.secondary}
                                    onClick={this._handlePayWithApp}
                                  />
                                </React.Fragment>
                              )}
                            </React.Fragment>
                          )}
                        </React.Fragment>
                      )}

                      {state.fields.customer_autogiro_single_amount.options[2]
                        .checked &&
                        state.swishStatus === "CREATED" && (
                          <React.Fragment>
                            <img
                              src={state.qrImageURL}
                              alt={"Swisha valfritt belopp"}
                              title={"Swisha valfritt belopp"}
                            />
                            <strong>
                              Skanna koden ovan i din Swishapp to pay{" "}
                              {
                                this.state.fields
                                  .customer_autogiro_single_amount_other
                              }{" "}
                              kr
                            </strong>
                            {this.isMobileOrTablet && (
                              <React.Fragment>
                                <div>
                                  <p>OR</p>
                                </div>
                                <OpenSwishButton
                                  accentColor={this.accentColors.secondary}
                                  onClick={this._handlePayWithApp}
                                />
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        )}
                    </div>
                  </div>
                )}
            </React.Fragment>
          )}

          <CheckboxWithName
            type="radio"
            style={{marginTop: "20px"}}
            label={checkboxMonthlyText}
            name="checkbox_autogiro_monthly"
            value={
              state.fields.checkbox_autogiro_monthly.checked ? "yes" : "no"
            }
            checked={state.fields.checkbox_autogiro_monthly.checked}
            onChange={this._onCheckboxChange}
            disabled={!state.swishFieldsEnabled}
          />

          {state.fields.checkbox_autogiro_monthly.checked && (
            <div className={"amount-options"}>
              <OptionsList
                type="radio"
                orientation="horizontal"
                name="customer_autogiro_monthly_amount"
                options={state.fields.customer_autogiro_monthly_amount.options}
                onChange={this._onOptionsListChange}
              />

              {state.fields.customer_autogiro_monthly_amount.options[2]
                .checked && (
                <div
                  style={{
                    margin: "20px 0 0 0",
                    display: "block",
                    maxWidth: "200px"
                  }}
                >
                  <strong style={{margin: "0 0 15px 0", display: "block"}}>
                    Ange ett belopp
                  </strong>
                  <EditableField
                    Component={NumberField}
                    name="customer_autogiro_monthly_amount_other"
                    label="Valfritt belopp"
                    value={state.fields.customer_autogiro_monthly_amount_other}
                    onChange={this._onFieldChange}
                    onBlur={this._onFieldBlur}
                    unit={"kr"}
                  />
                </div>
              )}

              <input
                type={"hidden"}
                name={"radio_autogiro_recurring"}
                value={"monthly"}
              />
              <input
                type={"hidden"}
                name={"customer_autogiro_amount_recurring"}
                value={amountMonthly + " kr"}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  _fieldValidator = (name, value) => {
    return value.length > 0;
  };

  _onFieldChange = (name, value) => {
    if (
      name === "customer_autogiro_single_amount_other" &&
      this.state.inputErrors[name]
    ) {
      // clear any input errors on input change
      this.setState({
        ...this.state,
        inputErrors: {
          ...this.state.inputErrors,
          customer_autogiro_single_amount_other: false
        }
      });
    }

    CobHelpers.onViewFieldChange.call(this, name, value);
  };

  _onFieldBlur = name => {
    CobHelpers.onViewFieldBlur.call(this, name);
  };

  _onCheckboxChange = async (name, checked) => {
    if (name === "checkbox_autogiro_monthly") {
      console.log("checkbox_autogiro_monthly");
      if (!this.swishSettings.requirePersonalInfo) {
        await this.props.toggleVisibleSection("CustomerInformation", checked);
      }
      await this.props.toggleVisibleSection("AccountDetails", checked);
      this.setState({
        fields: {
          ...this.state.fields,
          checkbox_autogiro_single: {
            checked: false
          },
          checkbox_autogiro_monthly: {
            checked: true
          }
        }
      });
    } else if (name === "checkbox_autogiro_single") {
      // clear any existing payment status check intervals
      window.clearInterval(this.paymentStatusCheck);

      console.log("checkbox_autogiro_single");
      if (!this.swishSettings.requirePersonalInfo) {
        await this.props.toggleVisibleSection("CustomerInformation", false);
      }

      await this.props.toggleVisibleSection("AccountDetails", false);
      this.setState({
        showSwish: false,
        qrImageURL: "",
        swishStatus: "NOT-LOADED",
        fields: {
          ...this.state.fields,
          customer_autogiro_single_amount_other: "",
          customer_autogiro_single_amount: {
            options: [
              {label: "100 kr", value: "100", checked: false},
              {label: "500 kr", value: "500", checked: false},
              {label: "Valfritt", value: "other", checked: false}
            ]
          },
          checkbox_autogiro_single: {
            checked: true
          },
          checkbox_autogiro_monthly: {
            checked: false
          }
          // [name]: { ...this.state.fields[name], checked }
        }
      });
    }
  };

  _onOptionsListChange = ({type, name}, option, index) => {
    const newOptions = [...this.state.fields[name].options].map((o, i) => {
      if (i === index) {
        return option;
      } else {
        return {...o, checked: type === "radio" ? false : o.checked};
      }
    });

    if (name === "customer_autogiro_monthly_amount") {
      this.setState(
        {
          fields: {
            ...this.state.fields,
            [name]: {
              options: newOptions
            }
          }
        },
        () => {
          fulfillmentCheck(this.props, this.state);
        }
      );
    } else if (name === "customer_autogiro_single_amount") {
      // clear any existing payment status check intervals
      window.clearInterval(this.paymentStatusCheck);

      this.setState(
        {
          showSwish: false,
          swishStatus:
            option.value === "100" || option.value === "500"
              ? "LOADED"
              : "NOT-LOADED",
          fields: {
            ...this.state.fields,
            [name]: {
              options: newOptions
            }
          }
        },
        () => {
          fulfillmentCheck(this.props, this.state);
          if (option.value === "100" || option.value === "500") {
            this._showSwish();
          }
        }
      );
    }
  };

  _handleResetSwish = () => {
    window.clearInterval(this.paymentStatusCheck);
    this.setState({
      showSwish: false,
      token: "",
      identifier: "",
      qrImageURL: "",
      swishStatus: "NOT-LOADED",
      fields: {
        ...this.state.fields,
        customer_autogiro_single_amount_other: "",
        customer_autogiro_single_amount: {
          options: [
            {label: "100 kr", value: "100", checked: false},
            {label: "500 kr", value: "500", checked: false},
            {label: "Valfritt", value: "other", checked: false}
          ]
        }
      },
      validFields: {
        ...this.state.validFields,
        customer_autogiro_single_amount_other: false
      },
      inputErrors: {
        ...this.state.inputErrors,
        customer_autogiro_single_amount_other: false
      }
    });
  };

  _handleCustomAmountSubmit = () => {
    if (!this.state.validFields.customer_autogiro_single_amount_other) {
      return this.setState({
        ...this.state,
        inputErrors: {
          ...this.state.inputErrors,
          customer_autogiro_single_amount_other: true
        }
      });
    }

    this.setState(
      {
        ...this.state,
        swishStatus: "LOADED"
      },
      () => this._showSwish()
    );
  };

  _showSwish = () => {
    const amount = _getPaymentAmount.call(this);
    _postQRPayment.call(this, amount, "Donation"); // TODO: get this text from config
  };

  _handlePayWithApp = () => {
    // set triggerSwishAppURL
    let triggerSwishAppURL = `swish://paymentrequest?token=${this.state.token}`;
    triggerSwishAppURL = this.swishSettings.redirectMobileApp
      ? triggerSwishAppURL +
        `&callbackurl=${this.frontEndURL}?id=${this.state.identifier}`
      : triggerSwishAppURL;

    this.setState({
      ...this.state,
      swishStatus: "APP-STARTED"
    });

    // start mobile app
    return (window.location = triggerSwishAppURL);
  };
}

// PRIVATE FUNCTIONS

function _getPaymentAmount() {
  const _this = this;

  let amount = 0;
  const options = _this.state.fields.customer_autogiro_single_amount.options;

  options.forEach(option => {
    if (option.checked) {
      if (option.value !== "other") return (amount = option.value);
      return (amount =
        _this.state.fields.customer_autogiro_single_amount_other);
    }
  });

  return amount;
}

function _getPaymentStatus(id) {
  console.log("retrieving payment status");
  const _this = this;
  const url = _this.apiBaseURL + "paymentrequest?id=" + id;

  console.log(id);

  fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      key: _this.apiKey
    }
  })
    .then(function (response) {
      return response.json();
    })
    .then(async function (json) {
      // originalPaymentReference = json.result.paymentReference; // do we need this??
      if (json.result.status !== "CREATED") {
        window.clearInterval(_this.paymentStatusCheck);
        _this.payeePaymentReference = generateGUID().slice(0, -4).toUpperCase(); // reset payeePaymentReference
      }

      if (
        json.result.status === "PAID" &&
        _this.swishSettings.requirePersonalInfo
      ) {
        // save payment case data and send emails if required
        const customerInfo =
          _this.props.app.latestViewStates.CustomerInformation.fields;
        customerInfo.swish_payment_id = json.result.id;

        const payeeName = _this.props.app.partner._data.companyName;
        const amount = _getPaymentAmount.call(_this);

        const paymentData = {
          payerFirstName: customerInfo.customer_firstname,
          payerLastName: customerInfo.customer_lastname,
          payerEmail: customerInfo.customer_email,
          payeeName,
          amount
        };

        const sendNotificationEmail = _this.swishSettings.sendNotificationEmail;

        try {
          await api("/swish/save", {
            customerInfo,
            paymentData,
            sendNotificationEmail
          });
        } catch (e) {
          console.log(e);
        }
      }

      console.log("status: ", json.result.status);

      _this.setState({
        ..._this.state,
        swishStatus:
          // if mobile app started keep swishStatus as APP-STARTED instead of "CREATED"
          _this.state.swishStatus === "APP-STARTED" &&
          json.result.status === "CREATED"
            ? _this.state.swishStatus
            : json.result.status,
        swishPaymentId:
          // only pass a swishPaymentId if it has been paid
          json.result.status === "PAID" ? json.result.id : null
      });
    })
    .catch(function (error) {
      console.log("Request failure: ", error);
    });
}

function _postQRPayment(amount, message) {
  const _this = this;
  const url = _this.apiBaseURL + "paymentrequest";

  // if (!_this.isMobileOrTablet) {
  _this.setState({
    ..._this.state,
    showSwish: true
  });
  // }

  fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      key: _this.apiKey
    },
    body: JSON.stringify({
      payeePaymentReference: _this.payeePaymentReference,
      amount: amount,
      message: message
    })
  })
    .then(function (response) {
      if (response.status !== 200) {
        return console.log(
          `Request failure: Invalid response status ${response.status}`
        );
      }
      return response.json();
    })
    .then(function (json) {
      if (json) {
        const identifier = json.result.id;
        const token = json.result.paymentrequesttoken;

        // fetch QR code
        const url = _this.apiBaseURL + "qr?token=" + token;
        fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            key: _this.apiKey
          }
        })
          .then(function (response) {
            return response.blob();
          })
          .then(function (blob) {
            var objectURL = URL.createObjectURL(blob);
            _this.setState({
              ..._this.state,
              token,
              identifier,
              qrImageURL: objectURL,
              swishStatus: "CREATED"
            });

            // start pinging status
            _this.paymentStatusCheck = setInterval(function () {
              _getPaymentStatus.call(_this, identifier);
            }, _this.PAYMENT_STATUS_CHECK_INTERVAL_MS);
          })
          .catch(function (error) {
            console.log("Request failure: ", error);
          });
      }
    })
    .catch(function (error) {
      console.log("Request failure: ", error);
    });
}

function _getFields() {
  return [
    "checkbox_autogiro_single",
    "checkbox_autogiro_monthly",
    "customer_autogiro_single_amount",
    "customer_autogiro_monthly_amount",
    "customer_autogiro_single_amount_other",
    "customer_autogiro_monthly_amount_other"
  ];
}

function _isMobileOrTablet() {
  if (
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
      navigator.userAgent
    ) ||
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw(n|u)|c55\/|capi|ccwa|cdm|cell|chtm|cldc|cmd|co(mp|nd)|craw|da(it|ll|ng)|dbte|dcs|devi|dica|dmob|do(c|p)o|ds(12|d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(|_)|g1 u|g560|gene|gf5|gmo|go(\.w|od)|gr(ad|un)|haie|hcit|hd(m|p|t)|hei|hi(pt|ta)|hp( i|ip)|hsc|ht(c(| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i(20|go|ma)|i230|iac( ||\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|[a-w])|libw|lynx|m1w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|mcr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|([1-8]|c))|phil|pire|pl(ay|uc)|pn2|po(ck|rt|se)|prox|psio|ptg|qaa|qc(07|12|21|32|60|[2-7]|i)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h|oo|p)|sdk\/|se(c(|0|1)|47|mc|nd|ri)|sgh|shar|sie(|m)|sk0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h|v|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl|tdg|tel(i|m)|tim|tmo|to(pl|sh)|ts(70|m|m3|m5)|tx9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas|your|zeto|zte/i.test(
      navigator.userAgent.substr(0, 4)
    )
  )
    return true;
  return false;
}

function _getInitialState() {
  if (this.props.prevState) return {...this.props.prevState};

  return this.initialState;
}

class OpenSwishButton extends React.PureComponent {
  render() {
    const {accentColor, onClick} = this.props;

    return (
      <div>
        <Button
          label="Open Swish App"
          accentColor={accentColor}
          onClick={onClick}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/routes/Cob/views/AmountSelectionSwishMC/AmountSelectionSwishMC.js